import { useEffect, useState } from 'react';
import { IconButton, Tooltip, Typography } from '@mui/joy';
import { useAuth } from '../../../hooks/useAuth';

const scriptConfig = {
  type: 'text/javascript',
  src: 'https://cheil.atlassian.net/s/d41d8cd98f00b204e9800998ecf8427e-T/vf1kch/b/9/c95134bc67d3a521bb3f4331beb9b804/_/download/batch/com.atlassian.jira.collector.plugin.jira-issue-collector-plugin:issuecollector/com.atlassian.jira.collector.plugin.jira-issue-collector-plugin:issuecollector.js?locale=en-GB&collectorId=8efb60b8'
};

const descriptionTemplate = `1. Athena Report: [URL to Athena report]
2. Related Jira Ticket: [Jira ticket URL or ID]
3. 10x Bulk test of the page where the issue was detected: [To verify whether the issue occurs consistently]
4. Athena report for the live version of the page: [To verify whether the issue is related to a recent update]

------------------------------

Issue Description:
[Detailed description of the problem]

------------------------------

Affected URL(s):
1.
2.
3.

Steps to Reproduce:
1.
2.
3.

Expected Result: [What should happen]
Actual Result: [What happens instead]

Evidence:
- Screenshot(s)
- Video

------------------------------

For static pages on the EU server (please clearly state whether the page was copied successfully):
1. Switch to Card View
2. Copy the page
3. Navigate to /pl/test/athena/bug-reports/
4. Paste (More Actions dropdown)
5. Select "Paste without children"

For non-EU pages, PDPs, GNB, Footer, and any other cases where page copy is not possible:
1. Open page
2. View Page Source
3. Save As
4. Select "Web Page, HTML Only"
5. Attach the saved HTML file to the bug report`;

type ExtendedWindow = Window &
  typeof globalThis & {
    ATL_JQ_PAGE_PROPS: {
      fieldValues: {
        email: string;
        description: string;
      };
    };
  };

export function JiraIssueCollector() {
  const [isReady, setIsReady] = useState(false);
  const { email } = useAuth();

  useEffect(() => {
    if (email) {
      (window as ExtendedWindow).ATL_JQ_PAGE_PROPS = {
        fieldValues: {
          email,
          description: descriptionTemplate
        }
      };
    }

    const script = document.createElement('script');
    script.type = scriptConfig.type;
    script.src = scriptConfig.src;

    script.onload = () => setIsReady(true);
    script.onerror = () => console.error('❌ Failed to load Jira collector script');

    document.body.appendChild(script);

    // cleanup
    return () => {
      if (script.parentNode) {
        document.body.removeChild(script);
      }

      const collectorElements = document.querySelectorAll('.atlwdg-trigger, #atlwdg-container, #atlwdg-blanket');
      collectorElements.forEach((el) => el.remove());

      setIsReady(false);
    };
  }, [email]);

  const handleClick = () => {
    const collectorBtn = document.querySelector('.atlwdg-trigger') as HTMLElement;
    collectorBtn?.click();
  };

  return (
    <Tooltip title={isReady ? 'Report an Issue' : 'Issue collector loading...'}>
      <IconButton
        variant="outlined"
        onClick={handleClick}
        disabled={!isReady}
        sx={{
          padding: 1,
          marginRight: 1,
          color: isReady ? '#000000' : '#cccccc',
          '&:hover': {
            backgroundColor: isReady ? 'rgba(0, 0, 0, 0.04)' : 'transparent'
          },
          '&.Mui-disabled': {
            color: '#cccccc'
          }
        }}
      >
        <Typography level="body-md">Report a Bug</Typography>
      </IconButton>
    </Tooltip>
  );
}
